/**
 * AdminSupport — admin-side ticket inbox at `/admin/support`.
 *
 * Lists tickets with filter chips (open / pending / resolved / closed),
 * shows the user account status (active / suspended / banned) beside the
 * ticket so admins can act on it without leaving the page, opens a thread
 * panel to reply + change status.
 */
import { useMemo, useState } from "react";
import { LifeBuoy, Filter, MessageSquare, RefreshCw, CheckCircle2, X, ShieldAlert, MessageCircle, AlertOctagon } from "lucide-react";
import {
    TICKET_STATUS,
    getAllTickets,
    getTicketMessages,
    replyToTicket,
    updateTicketStatus,
} from "@/lib/supportStore";
import { ACCOUNT_STATUS, getEffectiveStatus } from "@/lib/moderationStore";
import { getUsers } from "@/lib/storage";

const FILTERS = [
    { id: "all", label: "All" },
    { id: TICKET_STATUS.OPEN, label: "Open" },
    { id: TICKET_STATUS.PENDING, label: "Pending" },
    { id: TICKET_STATUS.RESOLVED, label: "Resolved" },
    { id: TICKET_STATUS.CLOSED, label: "Closed" },
];

const STATUS_BADGE = {
    [TICKET_STATUS.OPEN]: "bg-primary/10 text-primary border-primary/30",
    [TICKET_STATUS.PENDING]: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
    [TICKET_STATUS.RESOLVED]: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    [TICKET_STATUS.CLOSED]: "bg-muted text-muted-foreground border-border",
};

const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString(); }
    catch { return iso; }
};

const userStatusBadge = (user) => {
    const s = user ? getEffectiveStatus(user) : ACCOUNT_STATUS.ACTIVE;
    if (s === ACCOUNT_STATUS.BANNED) {
        return { label: "Banned", className: "text-destructive border-destructive/30 bg-destructive/10", icon: ShieldAlert };
    }
    if (s === ACCOUNT_STATUS.SUSPENDED) {
        return { label: "Suspended", className: "text-amber-700 dark:text-amber-300 border-amber-500/30 bg-amber-500/10", icon: AlertOctagon };
    }
    return { label: "Active", className: "text-emerald-700 dark:text-emerald-300 border-emerald-500/30 bg-emerald-500/10", icon: CheckCircle2 };
};

const AdminSupport = () => {
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null); // ticket id
    const [v, setV] = useState(0);

    const usersById = useMemo(() => {
        const m = new Map();
        for (const u of getUsers()) m.set(u.id, u);
        return m;
    }, [v]);

    const tickets = useMemo(() => {
        const list = getAllTickets()
            .slice()
            .sort((a, b) => Date.parse(b.updated_at || 0) - Date.parse(a.updated_at || 0));
        if (filter === "all") return list;
        return list.filter((t) => t.status === filter);
    }, [filter, v]);

    const activeTicket = useMemo(() => tickets.find((t) => t.id === active) || null, [tickets, active]);

    const counts = useMemo(() => {
        const all = getAllTickets();
        return {
            all: all.length,
            [TICKET_STATUS.OPEN]: all.filter((t) => t.status === TICKET_STATUS.OPEN).length,
            [TICKET_STATUS.PENDING]: all.filter((t) => t.status === TICKET_STATUS.PENDING).length,
            [TICKET_STATUS.RESOLVED]: all.filter((t) => t.status === TICKET_STATUS.RESOLVED).length,
            [TICKET_STATUS.CLOSED]: all.filter((t) => t.status === TICKET_STATUS.CLOSED).length,
        };
    }, [v]);

    return (
        <div data-testid="admin-support" className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="font-display text-[22px] font-semibold tracking-tight inline-flex items-center gap-2">
                        <LifeBuoy size={18} className="text-primary" />
                        Support tickets
                    </h2>
                    <p className="text-[13px] text-muted-foreground">
                        Reply to user tickets and update their status.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="admin-support-refresh"
                    onClick={() => setV((x) => x + 1)}
                    className="h-10 px-3 rounded-xl bg-secondary border border-border text-[12.5px] font-medium hover:bg-muted inline-flex items-center gap-1.5"
                >
                    <RefreshCw size={13} />
                    Refresh
                </button>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
                <Filter size={13} className="text-muted-foreground" />
                {FILTERS.map((f) => {
                    const isActive = filter === f.id;
                    return (
                        <button
                            key={f.id}
                            data-testid={`admin-support-filter-${f.id}`}
                            onClick={() => setFilter(f.id)}
                            className={[
                                "h-8 px-2.5 rounded-full text-[11.5px] font-medium border",
                                isActive
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-secondary border-border hover:bg-muted",
                            ].join(" ")}
                        >
                            {f.label} <span className="opacity-60">({counts[f.id] ?? 0})</span>
                        </button>
                    );
                })}
            </div>

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                {tickets.length === 0 ? (
                    <div className="p-10 text-center text-[13px] text-muted-foreground" data-testid="admin-support-empty">
                        No tickets in this view.
                    </div>
                ) : (
                    <table className="w-full text-[13px]">
                        <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/50">
                            <tr>
                                <th className="px-3 py-2.5">Subject</th>
                                <th className="px-3 py-2.5 hidden md:table-cell">User</th>
                                <th className="px-3 py-2.5">Account</th>
                                <th className="px-3 py-2.5">Status</th>
                                <th className="px-3 py-2.5 hidden md:table-cell">Updated</th>
                                <th className="px-3 py-2.5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {tickets.map((t) => {
                                const ticketUser = usersById.get(t.user_id);
                                const acctBadge = userStatusBadge(ticketUser);
                                return (
                                    <tr key={t.id} data-testid={`admin-support-row-${t.id}`}>
                                        <td className="px-3 py-2.5">
                                            <div className="font-medium truncate">{t.subject}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">{t.category}</div>
                                        </td>
                                        <td className="px-3 py-2.5 hidden md:table-cell">
                                            <div className="text-[12px] truncate">{t.user_name || "—"}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">{t.user_email || ""}</div>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span
                                                data-testid={`admin-support-acct-${t.id}`}
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${acctBadge.className}`}
                                            >
                                                <acctBadge.icon size={11} />
                                                {acctBadge.label}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${STATUS_BADGE[t.status]}`}>
                                                {t.status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground text-[11.5px]">
                                            {formatDate(t.updated_at)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <button
                                                data-testid={`admin-support-open-${t.id}`}
                                                onClick={() => setActive(t.id)}
                                                className="h-8 px-2.5 rounded-lg bg-primary text-primary-foreground text-[11.5px] font-medium inline-flex items-center gap-1"
                                            >
                                                <MessageSquare size={11} />
                                                Open
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {activeTicket && (
                <TicketDrawer
                    ticket={activeTicket}
                    user={usersById.get(activeTicket.user_id)}
                    onClose={() => setActive(null)}
                    onChanged={() => setV((x) => x + 1)}
                />
            )}
        </div>
    );
};

const TicketDrawer = ({ ticket, user, onClose, onChanged }) => {
    const [draft, setDraft] = useState("");
    const [v, setV] = useState(0);
    const messages = useMemo(() => getTicketMessages(ticket.id), [ticket.id, v]);

    // We use an in-memory admin author so author_id is consistent.
    const adminAuthor = { id: "admin", fullName: "Support" };

    const sendReply = () => {
        if (!draft.trim()) return;
        replyToTicket({ ticket, author: adminAuthor, role: "admin", body: draft });
        setDraft("");
        setV((x) => x + 1);
        onChanged();
    };

    const setStatus = (status) => {
        updateTicketStatus(ticket.id, status);
        setV((x) => x + 1);
        onChanged();
    };

    return (
        <div
            data-testid="admin-support-drawer"
            className="fixed inset-0 z-[80] bg-black/55 flex items-center justify-end"
            onClick={onClose}
        >
            <div
                className="h-full w-full max-w-md bg-card border-l border-border shadow-card flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-4 py-3 border-b border-border flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="font-display text-[15px] font-semibold truncate">{ticket.subject}</div>
                        <div className="text-[11.5px] text-muted-foreground truncate">
                            {ticket.user_name || "User"} · {ticket.category}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="admin-support-drawer-close"
                        className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-4 py-3 border-b border-border bg-muted/30">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
                        Account status
                    </div>
                    <AcctSummary user={user} />
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
                    {messages.map((m) => (
                        <div
                            key={m.id}
                            data-testid={`admin-support-msg-${m.id}`}
                            className={[
                                "max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[13px]",
                                m.author_role === "admin"
                                    ? "ml-auto bg-primary text-primary-foreground"
                                    : "bg-secondary border border-border",
                            ].join(" ")}
                        >
                            <div className="text-[10.5px] uppercase tracking-wider opacity-75 mb-0.5">
                                {m.author_role === "admin" ? "You (Support)" : "User"} · {formatDate(m.created_at)}
                            </div>
                            <div className="whitespace-pre-wrap">{m.body}</div>
                        </div>
                    ))}
                </div>

                <div className="border-t border-border p-3 space-y-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                            data-testid="admin-support-status-pending"
                            onClick={() => setStatus(TICKET_STATUS.PENDING)}
                            className="h-8 px-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[11.5px] font-medium"
                        >
                            Mark Pending
                        </button>
                        <button
                            data-testid="admin-support-status-resolved"
                            onClick={() => setStatus(TICKET_STATUS.RESOLVED)}
                            className="h-8 px-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[11.5px] font-medium"
                        >
                            Resolve
                        </button>
                        <button
                            data-testid="admin-support-status-closed"
                            onClick={() => setStatus(TICKET_STATUS.CLOSED)}
                            className="h-8 px-2.5 rounded-lg border border-border bg-secondary text-[11.5px] font-medium"
                        >
                            Close
                        </button>
                        <button
                            data-testid="admin-support-status-open"
                            onClick={() => setStatus(TICKET_STATUS.OPEN)}
                            className="h-8 px-2.5 rounded-lg border border-primary/30 bg-primary/10 text-primary text-[11.5px] font-medium"
                        >
                            Re-open
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            data-testid="admin-support-reply-input"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && sendReply()}
                            placeholder="Reply to user…"
                            className="flex-1 h-11 px-3.5 rounded-xl bg-secondary border border-border focus:border-primary focus:bg-background outline-none text-[13.5px]"
                        />
                        <button
                            type="button"
                            data-testid="admin-support-reply-send"
                            onClick={sendReply}
                            className="h-11 px-4 rounded-xl bg-primary text-primary-foreground text-[13px] font-medium"
                        >
                            Send
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AcctSummary = ({ user }) => {
    if (!user) {
        return <div className="text-[12px] text-muted-foreground">User no longer exists in this database.</div>;
    }
    const s = getEffectiveStatus(user);
    const badge = userStatusBadge(user);
    return (
        <div className="space-y-1.5 text-[12px]">
            <div className="inline-flex items-center gap-1.5">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${badge.className}`}>
                    <badge.icon size={11} />
                    {badge.label}
                </span>
                {(user.messaging_status || "active") === "suspended" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                        <MessageCircle size={11} />
                        Messaging suspended
                    </span>
                )}
            </div>
            {s === ACCOUNT_STATUS.BANNED && user.ban_reason && (
                <div className="text-muted-foreground">Ban reason: {user.ban_reason}</div>
            )}
            {s === ACCOUNT_STATUS.SUSPENDED && (
                <>
                    {user.suspension_reason && (
                        <div className="text-muted-foreground">Reason: {user.suspension_reason}</div>
                    )}
                    {user.suspended_until && (
                        <div className="text-muted-foreground">
                            Until: {formatDate(user.suspended_until)}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AdminSupport;
